import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import actionStatus from '@/client/actions/actionStatus';
import useFixFragmentSorting from '@/client/hooks/useFixFragmentSorting';
import useViewTransition from '@/client/hooks/useViewTransition';

import { nextPageProps } from '@/HOC/utils/nextProps';

import AppShell from '@/HOC/components/AppShell';

import getCanonicalUrl from '@/HOC/utils/seo/getCanonicalUrl';
import getBreadcrumbList from '@/HOC/utils/seo/getBreadcrumbList';
import getItemOrProductSchema from '@/HOC/utils/seo/getItemOrProductSchema';
import getImageObjectSchema from '@/HOC/utils/seo/getImageObjectSchema';
import categoryTypes from '@/server/constants/categoryTypes';
import getIsMobile from '@/client/utils/browser/isMobile';
import CATEGORIES_L1 from '@/constants/categories';

import updateDataLayerAndInitGA from '@/HOC/utils/updateDataLayerAndInitGA';
import fetchSearchResultsAndUpdateState from '@/HOC/utils/fetchSearchResultsAndUpdateState';
import { TRelevantInlink } from '@ecg-marktplaats/express-react/dist/types';
import isSsr from '@/client/utils/isSsr';
import LrpContainer from '@/client/containers/pages/LrpContainer';
import experiments from '@/utils/labs/experiments';
import getSpeakableSchema from './utils/seo/getSpeakableSchema';
import trackKeywordSearch from '@/client/utils/analytics/trackKeywordSearch';
import useIsMarktplaats from '@/client/hooks/useIsMarktplaats';
import getCollectionPageSchema from './utils/seo/getCollectionPageSchema';
import getLocalBusinessSchema from './utils/seo/getLocalBusinessSchema';
import locationData from '@/server/constants/locationData';

interface LrpProps {
  requestStatus: string;
  searchRequestAndResponse: TSearchRequestAndResponse;
  headerProps: THeaderProperties;
  categories: TCategories;
  seller: TSeller;
  relevantInlinks: TRelevantInlink[];
  paaContent?: { questions: Record<string, string>; answers: Record<string, string> };
  hubPage?: string;
  location?: string;
  cityIntroText?: string;
  priceSuggestions: any[];
  page: number;
  meta: TSeoMetaTags;
  hrefLangUrl: string;
  bannersConfig: TBannerServerData;
  shouldTrackHomepageSearch?: boolean;
  categoryRecommendation?: any;
}

const WrappedLRP = ({
  requestStatus = actionStatus.initial,
  searchRequestAndResponse,
  headerProps,
  categories,
  seller,
  bannersConfig,
  shouldTrackHomepageSearch,
  categoryRecommendation,
  ...props
}: LrpProps) => {
  const { tenantContext, labsConfig } = useContext(EnvironmentContext);
  const { isSortingEnabled, isViewTransitionsEnabled } = experiments({ labsConfig });
  const isMarktplaats = useIsMarktplaats();
  const { startTransition } = useViewTransition({ enabled: isViewTransitionsEnabled });

  const didMountRef = useRef(false);

  const [requestStatusState, setRequestStatusState] = useState(requestStatus);
  const [searchRequestAndResponseState, setSearchRequestAndResponseState] = useState(searchRequestAndResponse);
  const isCarsCategory = Boolean(searchRequestAndResponse.categoriesById[CATEGORIES_L1.CARS.toString()]);
  const isWomenClothingCategory = Boolean(
    searchRequestAndResponse.categoriesById[CATEGORIES_L1.DAMES_KLEREN.toString()],
  );

  const router = useRouter();
  const { query, asPath } = router;
  const { locale } = tenantContext;
  const withAttributesSorting = isSortingEnabled && !isWomenClothingCategory;

  useFixFragmentSorting(withAttributesSorting);

  useEffect(() => {
    if (isSsr()) return;
    const handleRouteChange = () => {
      if (isCarsCategory && !getIsMobile() && window.preventScroll) {
        window.preventScroll = false;
        return;
      }
      window.scrollTo(0, 0);
    };

    router.events.on('routeChangeStart', handleRouteChange);
    router.events.on('hashChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('hashChangeStart', handleRouteChange);
    };
  }, [router, isCarsCategory]);

  const fetchSearchResults = useCallback(async () => {
    await startTransition(async () => {
      await fetchSearchResultsAndUpdateState(query, locale, setRequestStatusState, setSearchRequestAndResponseState);
    });
  }, [locale, query, startTransition]);

  const extractQueryFromHash = useCallback((): string => {
    const hashMatch = window.location.hash.match(/#q:([^&]+)/);
    return hashMatch ? decodeURIComponent(hashMatch[1]) : '';
  }, []);

  const extractQueryFromPath = useCallback((): string => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const queryIndex = pathParts.indexOf('q');
    return queryIndex !== -1 && pathParts[queryIndex + 1] ? decodeURIComponent(pathParts[queryIndex + 1]) : '';
  }, []);

  const cleanUpUrlParameter = useCallback((paramName: string): void => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(paramName);
    const newSearch = urlParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }, []);

  const handleSearchTracking = useCallback(
    (recommendation: any) => {
      if (!isMarktplaats) return;

      cleanUpUrlParameter('trackSearch');

      const searchQuery = extractQueryFromHash() || extractQueryFromPath();
      if (searchQuery) {
        trackKeywordSearch({
          searchQuery,
          recommendedCategory: recommendation,
        });
      }
    },
    [extractQueryFromHash, extractQueryFromPath, cleanUpUrlParameter, isMarktplaats],
  );

  useEffect(() => {
    if (isSsr()) return;

    const urlParams = new URLSearchParams(window.location.search);
    const shouldTrackSearch = urlParams.get('trackSearch') === '1';
    if (shouldTrackSearch) {
      cleanUpUrlParameter('trackSearch');
    }

    if (didMountRef.current) {
      fetchSearchResults();
      return;
    }

    didMountRef.current = true;

    if (shouldTrackSearch || shouldTrackHomepageSearch) {
      handleSearchTracking(categoryRecommendation);
    }

    if (window.location.hash.length) {
      fetchSearchResults();
    } else {
      updateDataLayerAndInitGA(searchRequestAndResponseState, locale, {
        categoryRecommendation,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, asPath, fetchSearchResults, handleSearchTracking]);

  const {
    attributeHierarchy,
    searchRequest,
    searchCategory,
    searchCategoryOptions,
    seoFriendlyAttributes,
    alternativeLocales,
  } = searchRequestAndResponseState;

  return (
    <AppShell
      headerProps={headerProps}
      searchRequest={searchRequest}
      attributeHierarchy={attributeHierarchy}
      seoFriendlyAttributes={seoFriendlyAttributes}
      searchCategory={searchCategory}
      searchCategoryOptions={searchCategoryOptions}
      alternativeLocales={alternativeLocales}
      seller={seller}
    >
      <LrpContainer
        {...props}
        requestStatus={requestStatusState}
        searchRequestAndResponse={searchRequestAndResponseState}
        headerProps={headerProps}
        categories={categories}
        seller={seller}
        bannersConfig={bannersConfig}
      />
    </AppShell>
  );
};

WrappedLRP.getInitialProps = ({ res, req }) => {
  // when the user presses the back button, this getInitialProps gets called.
  // No req/res is available, so we need to reconstruct the returned props from another source
  if (process.browser && !req && !res) {
    return {
      ...nextPageProps(),
    };
  }

  const { hostname, originalUrl } = req;
  const hrefLangUrl = `https://${hostname}${originalUrl}`;

  const {
    pagination,
    headerProperties,
    searchRequestAndResponse,
    eligibleForCategoryRedirectServerSide,
    relevantInlinks,
    priceSuggestions,
    bannersConfig,
    requestData,
    seller,
    shouldTrackHomepageSearch,
    categoryRecommendation,
    seoProperties,
    location: locationSlug,
    locationStats,
  } = res.locals;

  const fallbackImageUrl = seoProperties?.fallbackImageUrl || '';

  const page = pagination ? pagination.offset / pagination.limit : null;

  const initialProps = {
    headerProps: headerProperties,
    hrefLangUrl,
    ...(pagination && { page }),
  };

  let dominantCategory: TDominantCategory | undefined;
  if (!searchRequestAndResponse) {
    initialProps.searchRequestAndResponse = {};
    initialProps.requestStatus = actionStatus.failure;
  } else {
    initialProps.searchRequestAndResponse = searchRequestAndResponse;
    initialProps.requestStatus = actionStatus.success;
    initialProps.paaContent = searchRequestAndResponse.paaContent;
    initialProps.hubPage = searchRequestAndResponse.hubPage;
    initialProps.location = locationSlug;
    initialProps.eligibleForCategoryRedirectServerSide = eligibleForCategoryRedirectServerSide;
    initialProps.relevantInlinks = relevantInlinks;
    initialProps.priceSuggestions = priceSuggestions;
    initialProps.shouldTrackHomepageSearch = shouldTrackHomepageSearch;
    initialProps.categoryRecommendation = categoryRecommendation;
    initialProps.cityIntroText = locationSlug ? locationData[locationSlug]?.cityIntro : undefined;
    initialProps.cityName = locationSlug ? (locationData[locationSlug]?.name ?? null) : null;
    initialProps.locationAvgPriceCents = locationSlug ? (locationData[locationSlug]?.avgPriceCents ?? null) : null;
    initialProps.locationStats = locationStats;

    const categoryTree = searchRequestAndResponse.facets.find(({ type }) => type === categoryTypes.CategoryTreeFacet);

    if (categoryTree && categoryTree.categories) {
      dominantCategory = categoryTree.categories && categoryTree.categories.find((obj) => obj.dominant);
    }
  }
  const listings = searchRequestAndResponse ? searchRequestAndResponse.listings : [];
  const locale = requestData.tenant.locale;
  const { metaTitle, metaDescription } = searchRequestAndResponse?.metaTags ?? {};

  const canonicalUrl = getCanonicalUrl(req, dominantCategory);
  const { l1Category, l2Category, location } = res.locals;

  const collectionPage =
    l1Category || l2Category
      ? getCollectionPageSchema(
          canonicalUrl,
          metaTitle || '',
          metaDescription || '',
          requestData.tenant.locale,
          req.hostname,
        )
      : null;

  const siteName = res.locals.i18n?.t('breadcrumbs.platform') || '';
  const countryCode = requestData.tenant.locale.split('-')[1] || '';

  const localBusiness =
    location && !l2Category && locationData[location] && l1Category?.name
      ? getLocalBusinessSchema(siteName, countryCode, l1Category.name, locationData[location].name, canonicalUrl)
      : null;

  const meta = {
    canonicalUrl,
    breadcrumbs: getBreadcrumbList(req, res),
    product: getItemOrProductSchema(req, res, fallbackImageUrl ? fallbackImageUrl : ''),
    imageObject: getImageObjectSchema(listings, locale),
    speakable: getSpeakableSchema(metaTitle, metaDescription, locale, seller),
    ...(collectionPage && { collectionPage }),
    ...(localBusiness && { localBusiness }),
  } as TSeoMetaTags;

  initialProps.meta = meta;
  initialProps.bannersConfig = bannersConfig;
  return initialProps;
};

export default WrappedLRP;
